import React, { useEffect, useCallback, useState, useMemo } from 'react'
import Theme from '@refera/ui-core'
import { useDispatch, useSelector } from 'react-redux'
import { Button, Typography, Loader } from '@refera/ui-web'
import { FilterSearch as FilterIcon, DocumentDownload } from '@refera/ui-icons'
import { Grid } from '@material-ui/core'
import Box from '@mui/material/Box'
import Tooltip from '@mui/material/Tooltip'
import useIsGenericParamActive from '_hooks/use-is-generic-param-active'

import { getCities } from '_modules/utils/actions'
import { getUserAgents } from '_modules/responsible/actions'
import { getUser, updateUserState } from '_modules/authentication/actions'
import { getAgencies, getAgencyResponsible } from '_modules/agency/actions'
import {
  getStepStatusFilter,
  getServiceOrders,
  setDefaultServiceOrderFilter,
  updateServiceOrderFilter,
  getGenericParameters,
} from '_modules/service-orders/actions'

import {
  getServiceOrderFilterSelector,
  getServiceOrderIsFilterDirtySelector,
  getServiceOrderLoadingSelector,
  getStepStatusFilterLoadingSelector,
  getServiceOrdersResults,
} from '_modules/service-orders/selectors'
import { userSelector } from '_modules/authentication/selectors'

import useToggle from '_hooks/use-toggle'
import useRequest from '_/hooks/use-request'
import useRolePermission from '_hooks/use-role-permission'

import { AGENCY_TYPES_OPTIONS } from '_utils/constants'
import { GENERIC_PARAMETERS } from '_utils/constants/service-order'

import RatingModal, { WARNING_MODAL } from '_components/modal/rating-modal'
import Loading from '_components/loading'
import {
  ServiceOrderMainTable,
  ServiceOrderMainViewFilter,
  FILTER_OPTIONS,
} from '_components/service-order'

import useStyles from './styles'
import { downloadReport, downloadFilteredReports } from '_/services/agency'
import { PERMISSIONS, PERMISSIONS_ACTIONS } from '_/utils/constants/permissions'
import { useToast } from '_/hooks/use-toast'
import { exportToXLSX } from '_/utils/export-to-excel'
import moment from 'moment'
import { useDialog } from '_/hooks/use-dialog'
import * as Modal from '_components/modal/generic-modal'
import { NewServiceOrderButton } from './new-service-order-button'

const EXPORT_DISABLED_TOOLTIP = 'Funcionalidade temporariamente desabilitada.'

function ExportButton({ enabled, loading, onClick, children }) {
  if (!enabled && !loading) {
    return (
      <Tooltip title={EXPORT_DISABLED_TOOLTIP} placement="top">
        <span>
          <Modal.ButtonFullBlue disabled>{children}</Modal.ButtonFullBlue>
        </span>
      </Tooltip>
    )
  }
  return (
    <Modal.ButtonFullBlue disabled={loading || !enabled} onClick={onClick}>
      {children}
    </Modal.ButtonFullBlue>
  )
}

// TODO: Rename component and folder once this view is ready to Production. Also, remove the older view/components.
const ManagementServiceOrder = () => {
  const styles = useStyles()
  const dispatch = useDispatch()

  const isStepStatusFilterLoading = useSelector(getStepStatusFilterLoadingSelector)
  const user = useSelector(userSelector)
  const filters = useSelector(getServiceOrderFilterSelector)
  const isFilterDirty = useSelector(getServiceOrderIsFilterDirtySelector)
  const [firstPageLoad, setFirstPageLoad] = useState(true)
  const { isAdmin, isIntermediary, isSAAS, checkUserPermission } = useRolePermission()

  const [ratingModal, setRatingModal] = useState({
    isOpen: false,
    title: '',
    subTitle: '',
  })

  const [page, setPage] = useState(filters?.page || 0)
  const [pageSize, setPageSize] = useState(filters?.pageSize || 10)
  const [orderBy, setOrderBy] = useState(filters?.orderBy || '')
  const [isFilterOpen, handleFilterOpen] = useToggle()

  const [isGetUserLoading] = useRequest(getUser())

  const isGetServiceOrdersLoading = useSelector(getServiceOrderLoadingSelector)

  const [isExporting, setIsExporting] = useState(false)

  const chipColor = useMemo(
    () => (user.viewOnlyMyCompanyTickets ? styles.chipEnabled : styles.chipDisabled),
    [user.viewOnlyMyCompanyTickets]
  )
  const isLoading = useMemo(
    () => isGetServiceOrdersLoading || isGetUserLoading,
    [isGetServiceOrdersLoading, isGetUserLoading]
  )

  const agencyDefaultValue = useMemo(() => {
    if (isAdmin) {
      return AGENCY_TYPES_OPTIONS[0]?.value
    }
    return ''
  }, [isAdmin])

  const responsibleDefaultValue = useMemo(
    () => (isIntermediary ? user?.id : ''),
    [filters?.[FILTER_OPTIONS.RESPONSIBLE]]
  )

  const defaultValues = useMemo(
    () => ({
      [FILTER_OPTIONS.AGENCY]: filters?.[FILTER_OPTIONS.AGENCY] || agencyDefaultValue,
      [FILTER_OPTIONS.CLASSIFICATION]: filters?.[FILTER_OPTIONS.CLASSIFICATION] || '',
      [FILTER_OPTIONS.CITY]: filters?.[FILTER_OPTIONS.CITY] || '',
      [FILTER_OPTIONS.FULL_ADDRESS]: filters?.[FILTER_OPTIONS.FULL_ADDRESS] || '',
      [FILTER_OPTIONS.SERVICEORDER_STATUS]: filters?.[FILTER_OPTIONS.SERVICEORDER_STATUS] || '',
      [FILTER_OPTIONS.PROPERTY_CODE]: filters?.[FILTER_OPTIONS.PROPERTY_CODE] || '',
      [FILTER_OPTIONS.REQUESTER_DATA]: filters?.[FILTER_OPTIONS.REQUESTER_DATA] || '',
      [FILTER_OPTIONS.RESPONSIBLE]: responsibleDefaultValue,
      [FILTER_OPTIONS.SERVICE_ORDER_ID]: filters?.[FILTER_OPTIONS.SERVICE_ORDER_ID] || '',
      [FILTER_OPTIONS.SITUATION]: filters?.[FILTER_OPTIONS.SITUATION] || '',
      [FILTER_OPTIONS.STEP_STATUS]: filters?.[FILTER_OPTIONS.STEP_STATUS] || '',
      [FILTER_OPTIONS.TRADESMAN]: filters?.[FILTER_OPTIONS.TRADESMAN] || '',
    }),
    [filters, agencyDefaultValue, responsibleDefaultValue]
  )

  const serviceOrdersResults = useSelector(getServiceOrdersResults)
  const { showToast } = useToast()
  const { showDialog, closeDialog } = useDialog()
  const [showModal, setShowModal] = useState(false)

  const enableDownloadAllChamados = useIsGenericParamActive({
    name: GENERIC_PARAMETERS.ENABLE_DOWNLOAD_REPORT,
  })
  const enableDownloadFiltered = useIsGenericParamActive({
    name: GENERIC_PARAMETERS.ENABLE_DOWNLOAD_REPORT_FILTER,
  })

  const openExportModal = useCallback(() => {
    dispatch(getGenericParameters({ name: GENERIC_PARAMETERS.ENABLE_DOWNLOAD_REPORT }))
    dispatch(getGenericParameters({ name: GENERIC_PARAMETERS.ENABLE_DOWNLOAD_REPORT_FILTER }))
    setShowModal(true)
  }, [dispatch])

  useEffect(() => {
    if (firstPageLoad) {
      if (!isFilterDirty) {
        dispatch(
          setDefaultServiceOrderFilter({
            isAdmin,
            isIntermediary,
            userId: user?.id,
            agency: AGENCY_TYPES_OPTIONS[0]?.value,
          })
        )
      }

      setFirstPageLoad(false)
    }
  }, [user?.getRoles, firstPageLoad, isAdmin, isIntermediary])

  useEffect(() => {
    if (!isGetServiceOrdersLoading && !firstPageLoad) {
      dispatch(
        getServiceOrders({
          ...filters,
          see_only_my_company_service_order: user?.viewOnlyMyCompanyTickets,
          page: page + 1,
          pageSize,
          order: orderBy,
        })
      )
      dispatch(updateServiceOrderFilter({ ...filters, page, pageSize, order: orderBy }))
    }
  }, [firstPageLoad, orderBy, page, pageSize, user?.getRoles])

  useEffect(() => {
    if (isGetUserLoading) return
    dispatch(getAgencies())
    dispatch(getCities())
    user?.agency && dispatch(getAgencyResponsible(user.agency))
    dispatch(getUserAgents())
    dispatch(getGenericParameters({ name: GENERIC_PARAMETERS.DELAYED }))
  }, [isGetUserLoading])

  useEffect(() => {
    if (!isStepStatusFilterLoading) {
      dispatch(getStepStatusFilter({ isFilterActive: user.viewOnlyMyCompanyTickets }))
    }
  }, [])

  const handleOrderBy = useCallback(orderObj => {
    const order = orderObj[0]

    if (!order) {
      setOrderBy('createdAt')
      return
    }

    const { field, sort } = order

    setOrderBy(`${sort === 'desc' ? '-' : ''}${field}`)
  }, [])

  const handleDownloadReport = useCallback(async () => {
    try {
      const response = await downloadReport()
      const responseUrl = await fetch(response.url)
      const blob = await responseUrl.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'download-report.xlsx'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    } catch (err) {
      setRatingModal({
        isOpen: true,
        title: 'Atenção!',
        subTitle: 'Erro, não foi possível fazer o download no momento. Tente novamente mais tarde!',
        type: WARNING_MODAL,
      })
    }
    closeDialog()
  })

  const handleConfirmDownloadReport = useCallback(() => {
    showDialog({
      type: 'warning',
      subject: 'Você tem certeza que deseja baixar o relatório do chamados?',
      description:
        'Este relatório contém todos os chamados, e é atualizado diariamente durante a madrugada.',
      labelApprove: 'Sim',
      labelCancel: 'Não',
      onApprove: () => {
        handleDownloadReport()
      },
      onCancel: () => {
        closeDialog()
      },
    })
  }, [])

  const handleExportData = useCallback(async () => {
    if (!serviceOrdersResults || Object.keys(serviceOrdersResults).length === 0) {
      showToast({
        type: 'error',
        message: 'Não há nenhum registro para exportar',
      })
      return
    }

    setIsExporting(true)

    try {
      const queryParams = new URLSearchParams(
        Object.entries(filters)
          .filter(
            ([key, value]) => value != null && value !== '' && key !== 'page' && key !== 'pageSize'
          )
          .map(([key, value]) => [key.replace(/([A-Z])/g, '_$1').toLowerCase(), value])
          .concat([['see_only_my_company_service_order', user?.viewOnlyMyCompanyTickets]])
      )

      const response = await downloadFilteredReports(queryParams)

      const now = moment().format('DD-MM-YY_HH-mm')
      const fileName = `Chamados_${now}`

      if (!response?.data) {
        showToast({
          type: 'error',
          message: 'Não há dados para exportar',
        })
        return
      }

      exportToXLSX(response?.data, fileName)
    } catch (error) {
      console.error({ error })
      showToast({
        type: 'error',
        message: 'Não foi possível exportar os chamados, tente novamente.',
      })
    } finally {
      setIsExporting(false)
    }
  }, [serviceOrdersResults, filters])

  if (isLoading && firstPageLoad) {
    return <Loading />
  }

  const handleFilter = () => {
    const payload = {
      ...filters,
      see_only_my_company_service_order: !user.viewOnlyMyCompanyTickets,
    }

    dispatch(
      getServiceOrders({
        ...payload,
      })
    )
    dispatch(getStepStatusFilter({ isFilterActive: !user.viewOnlyMyCompanyTickets }))
    dispatch(updateUserState({ viewOnlyMyCompanyTickets: !user.viewOnlyMyCompanyTickets }))
  }

  return (
    <Grid className={styles.container}>
      <ServiceOrderMainViewFilter
        handleFilterOpen={handleFilterOpen}
        open={isFilterOpen}
        defaultValues={defaultValues}
        setPage={setPage}
      />
      <Grid className={styles.header}>
        <Typography variant="h4" component="h1" className={styles.title}>
          Gestão de chamados
        </Typography>
        <Grid className={styles.headerButton}>
          <NewServiceOrderButton />
          {(isIntermediary || isSAAS) && (
            <Grid>
              <Button
                disabled={isLoading}
                variant="ghost"
                startIcon={<DocumentDownload color={Theme.Colors.Primary.Base} />}
                onClick={() =>
                  checkUserPermission({
                    permission: PERMISSIONS.SERVICE_ORDER,
                    permissionAction: PERMISSIONS_ACTIONS.EDIT,
                    functionAccess: () => openExportModal(),
                  })
                }
              >
                Baixar relatório
              </Button>
              <button
                type="button"
                className={`${styles.chip} ${chipColor}`}
                onClick={() => handleFilter()}
              >
                Ver somente meus chamados da empresa
              </button>
            </Grid>
          )}
          <Button
            disabled={isLoading}
            variant="ghost"
            startIcon={<FilterIcon color={Theme.Colors.Primary.Base} />}
            onClick={handleFilterOpen}
          >
            Filtros
          </Button>
        </Grid>
      </Grid>
      <Grid className={styles.tableContainer}>
        <Grid className={styles.mainTable}>
          <ServiceOrderMainTable
            onPageChange={setPage}
            page={page}
            onPageSizeChange={setPageSize}
            pageSize={pageSize}
            onSortModelChange={handleOrderBy}
            defaultValues={defaultValues}
            user={user}
          />
        </Grid>
      </Grid>

      <Modal.Root open={showModal} onOpenChange={setShowModal} onClose={() => setShowModal(false)}>
        <Modal.Content className={styles.modalExport}>
          <Modal.DocumentCopyIcon />
          <Modal.TitleModal title="Exportar Chamados" />
          <Modal.Text fontWeight="bold">Selecione como deseja exportar os chamados.</Modal.Text>
        </Modal.Content>

        <Modal.Actions className={styles.modalExportActions}>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 1.5,
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              boxSizing: 'border-box',
              '& > span': {
                display: 'inline-flex',
                alignItems: 'center',
              },
            }}
          >
            <Modal.ButtonRed onClick={() => setShowModal(false)}>Cancelar</Modal.ButtonRed>
            <ExportButton enabled={enableDownloadAllChamados} onClick={handleConfirmDownloadReport}>
              Todos os chamados
            </ExportButton>
            <ExportButton enabled={enableDownloadFiltered} onClick={handleExportData}>
              Somente os filtrados
            </ExportButton>
          </Box>
        </Modal.Actions>
      </Modal.Root>
      <Loader hasBackdrop open={isExporting} label="Aguarde..." />
      <RatingModal ratingModal={ratingModal} setRatingModal={setRatingModal} />
    </Grid>
  )
}

export default ManagementServiceOrder
